import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceTemplateSelectionCount } from 'src/modules/invoice/models/invoiceTemplateSelectionCount';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getInvoiceTemplateSelectionCount } from 'src/shared/services/invoiceService';

interface InvoiceTemplateSelectionSummaryProps {
  templateId: number;
  // Urvalet i klartext, en rad per villkor.
  clauses: string[];
  // Autogiro- och swishmallar räknas inte här, se kommentaren vid knappen.
  showCount?: boolean;
  // Sparar mallen och svarar om det gick bra. Antalet räknas på den sparade mallen, så utan
  // sparning hade siffran gällt något annat än det man ser på skärmen.
  saveBeforeCount: () => Promise<boolean>;
}

/**
 * Sammanfattning av mallens urval, som i utskicksbeställningens Ditt urval.
 *
 * Villkoren är härledda ur formuläret och visar det som är valt just nu. Antalet räknas av API:et
 * med samma urvalslogik som fakturakörningen, och hämtas på begäran eftersom urvalet måste lösas
 * upp i en medlemslista. Siffran gäller mallen som den är sparad, inte osparade ändringar.
 */
export const InvoiceTemplateSelectionSummary = ({ templateId, clauses, showCount = true, saveBeforeCount }: InvoiceTemplateSelectionSummaryProps) => {
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();

  const [count, setCount] = useState<InvoiceTemplateSelectionCount | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const fetchCount = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const saved = await saveBeforeCount();

      if (!saved) {
        setFailed(true);
        return;
      }

      const result = await getInvoiceTemplateSelectionCount(templateId);
      if (isMounted()) setCount(result.data);
    } catch (err) {
      ConsoleHelper.log(err);
      if (isMounted()) setFailed(true);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
        {t('invoice:adminInvoiceTemplatePage.templateInformation.yourSelection')}
      </Typography>

      {clauses.length > 0 ? (
        <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
          {clauses.map((clause) => (
            <Typography key={clause} component='li' variant='body2' color='text.secondary'>
              {clause}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          {t('invoice:adminInvoiceTemplatePage.templateInformation.noConditions')}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/*
        Antalet räknas med den vanliga fakturakörningens urval. En autogiro- eller swishmall plockar
        sina medlemmar med en egen fråga som dessutom beror på dragningsdatumet, så en siffra här
        hade visat något annat än det autojobbet faktiskt skapar.
      */}
      {!showCount && (
        <Typography variant='body2' color='text.secondary'>
          {t('invoice:adminInvoiceTemplatePage.templateInformation.countNotApplicable')}
        </Typography>
      )}

      {showCount && (
        <>
          {count ? (
            <Box>
              <Typography variant='h4' sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {count.invoiceCount}{' '}
                <Typography component='span' variant='body2' color='text.secondary'>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.invoices')}
                </Typography>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('invoice:adminInvoiceTemplatePage.templateInformation.membersCovered', { count: count.memberCount })}
              </Typography>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
                {t('invoice:adminInvoiceTemplatePage.templateInformation.countIsSaved')}
              </Typography>
            </Box>
          ) : (
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              {t('invoice:adminInvoiceTemplatePage.templateInformation.countHelper')}
            </Typography>
          )}

          {failed && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
              {t('invoice:adminInvoiceTemplatePage.templateInformation.countFailed')}
            </Typography>
          )}

          <Button
            variant='outlined'
            size='small'
            onClick={fetchCount}
            disabled={loading || !templateId}
            startIcon={loading ? <CircularProgress size={14} /> : undefined}
            sx={{ mt: 1.5 }}
          >
            {t('invoice:adminInvoiceTemplatePage.templateInformation.countAction')}
          </Button>
        </>
      )}
    </Box>
  );
};
